import { memo, useRef, useState, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslator } from '@picsart/rc/services/localization';
import storageApi from '@picsart/rc/services/storage';
import IconSettings from '@picsart/icons/IconSettings';
import IconSun from '@picsart/icons/IconSun';
import IconMoon from '@picsart/icons/IconMoon';
import View from '@picsart/view/View';
import { SuggestionMenu, SuggestionMenuPositions, HorizontalPositions } from '@picsart/suggestionmenu';
import { ControlSizesMD, InlineButton } from '@picsart/inlineButton';

import { LOCAL_STORAGE_KEYS, LOCAL_STORAGE_PREFIX, REMOTE_THEMES } from 'configs/constants';
import { changeThemeColors } from 'utils/themes/colors';
import generalSlice from 'store/general';
import { isOSThemeSupported } from 'utils/ui-helpers';
import { getIsDarkModeEnabled, getIsOSThemeEnabled } from 'store/selectors/general';
import useMemoSelector from 'hooks/useMemoSelector';
import useClickOutside from 'hooks/common/useClickOutside';
import { zThemeSettings } from 'utils/ui-helpers/stackingContexts';

import ListItem from './ListItem';
import useStyles from './styles';
import testIds from './testIds';

const ThemeSettings = () => {
  const translate = useTranslator();
  const dispatch = useDispatch();
  const isDarkModeEnabled = useMemoSelector(getIsDarkModeEnabled);
  const isOSThemeEnabled = useMemoSelector(getIsOSThemeEnabled);
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const classes = useStyles();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const outsideClickHandler = (e: ChangeEvent<HTMLElement>) => {
    if (e.target !== buttonRef.current) {
      setIsThemeDropdownOpen(false);
    }
  };

  const ref = useClickOutside(outsideClickHandler);

  const handleClickThemeSettings = () => {
    setIsThemeDropdownOpen(!isThemeDropdownOpen);
  };

  const handleThemeChange = (theme: REMOTE_THEMES) => {
    setIsThemeDropdownOpen(false);

    if (theme === REMOTE_THEMES.System) {
      dispatch(generalSlice.actions.setUseOSThemeEnabled(true));
      storageApi.putSync(LOCAL_STORAGE_PREFIX, LOCAL_STORAGE_KEYS.theme, REMOTE_THEMES.System);
    } else {
      const isDark = theme === REMOTE_THEMES.Dark;
      document.head.appendChild(changeThemeColors({ isDark }));

      dispatch(generalSlice.actions.setIsDarkModeEnabled(isDark));
      dispatch(generalSlice.actions.setUseOSThemeEnabled(false));
      storageApi.putSync(LOCAL_STORAGE_PREFIX, LOCAL_STORAGE_KEYS.theme, isDark ? REMOTE_THEMES.Dark : REMOTE_THEMES.Light, false);
    }
  };

  return (
    <View className={classes.root}>
      <InlineButton
        data-testid={testIds.settingsBtn}
        className={classes.settingsButton}
        size={ControlSizesMD}
        onClick={handleClickThemeSettings}
        ref={buttonRef}
        centerIcon={IconSettings}
        isWithoutBorder
      />

      <SuggestionMenu
        className={classes.menu}
        ref={ref}
        relativeRef={buttonRef}
        isOpen={isThemeDropdownOpen}
        position={SuggestionMenuPositions.Top}
        alignment={HorizontalPositions.Left}
        zIndex={zThemeSettings}
      >
        { isOSThemeSupported() && (
          <ListItem
            dataTestId={testIds.systemBtn}
            isActive={isOSThemeEnabled}
            Icon={IconSettings}
            text={translate('System default')}
            onClick={() => handleThemeChange(REMOTE_THEMES.System)}
          />
        )}
        <ListItem
          dataTestId={testIds.darkBtn}
          isActive={!isOSThemeEnabled && isDarkModeEnabled}
          Icon={IconMoon}
          text={translate('Dark')}
          onClick={() => handleThemeChange(REMOTE_THEMES.Dark)}
        />
        <ListItem
          dataTestId={testIds.lightBtn}
          isActive={!isOSThemeEnabled && !isDarkModeEnabled}
          Icon={IconSun}
          text={translate('Light')}
          onClick={() => handleThemeChange(REMOTE_THEMES.Light)}
        />
      </SuggestionMenu>
    </View>
  );
};

export default memo(ThemeSettings);
